import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import { api } from "@/api/client";

// Shared authenticated-proxy image fetch — same pattern as WorkViewer.jsx's useProxyBlob /
// AgencyAvatar.jsx (never a raw <img src> to an endpoint that needs auth headers).
//
// RS-15 fix (2026-10, Rolled-Up Wave Part 2C) — a failed fetch (e.g. a transient Drive hiccup,
// now a clean 502 from the server instead of an unhandled 500 — see modules/build.py's
// _agency_serve_recce_photo) used to leave `objectUrl` null FOREVER with no error surfaced —
// the dialog's own black backdrop with nothing in it IS the "black screen" bug. `error` is now
// tracked and rendered (see the component below) instead of silently swallowed.
function useProxyBlob(url, retryToken) {
  const [state, setState] = useState({ objectUrl: null, loading: !!url, error: false });
  useEffect(() => {
    if (!url) { setState({ objectUrl: null, loading: false, error: false }); return undefined; }
    let active = true;
    let blobUrl = null;
    setState({ objectUrl: null, loading: true, error: false });
    api.get(url, { responseType: "blob", silent401: true }).then((r) => {
      if (!active) return;
      blobUrl = window.URL.createObjectURL(r.data);
      setState({ objectUrl: blobUrl, loading: false, error: false });
    }).catch(() => { if (active) setState({ objectUrl: null, loading: false, error: true }); });
    return () => { active = false; if (blobUrl) window.URL.revokeObjectURL(blobUrl); };
  }, [url, retryToken]);
  return state;
}

// Reusable multi-image lightbox (Agency Portal Restyle Wave 1, 2026-10) — built for the agency
// Behind the Scenes grid, kept generic (array of {url, caption} + index) so it isn't BTS-specific.
// `items[i].url` is fetched through the shared authenticated proxy, same as every other
// Drive-backed media view in this portal.
//
// Cursor fix, item 11 (2026-10) — a real modal dialog, not just a styled overlay:
//   * role="dialog" + aria-modal + an accessible name.
//   * focus moves INTO the dialog on open (the close button) and is RESTORED to whatever had
//     focus before opening (usually the thumbnail that was clicked) on close.
//   * a focus trap — Tab/Shift+Tab cycle only among the dialog's own buttons, never escaping
//     to the page behind it.
//   * body scroll lock while open, released on close.
export function Lightbox({ items, index, onClose, onNavigate }) {
  const containerRef = useRef(null);
  const previouslyFocused = useRef(null);
  const [retryToken, setRetryToken] = useState(0);
  // Regression v6 fix pass (2026-10), item 4 — a file can download perfectly and STILL not
  // render: HEIC/HEIF (every iPhone photo) has no browser decoder at all, so the fetch
  // succeeds, `<img>` fires onError, and the old code showed an empty overlay. Tracked
  // separately from `error` (a FETCH failure) because the remedy is different: a fetch error
  // gets Try again, an undecodable image gets Download.
  const [renderFailed, setRenderFailed] = useState(false);
  // Regression v6 Part 5 (2026-10) — Download is now permanent (see the button below), so it
  // has to work with no blob in hand too: it fetches the rendition itself in that case, and a
  // failed download gets its own Try again. Same behaviour as the staff viewer
  // (components/project/recce/RecceLightbox.jsx).
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  useEffect(() => { setRenderFailed(false); setDownloadFailed(false); }, [index, retryToken]);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const toFocus = containerRef.current?.querySelector('[data-testid="agency-lightbox-close"]');
    toFocus?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused.current && document.body.contains(previouslyFocused.current)) {
        previouslyFocused.current.focus();
      }
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowLeft") { onNavigate(Math.max(0, index - 1)); return; }
      if (e.key === "ArrowRight") { onNavigate(Math.min(items.length - 1, index + 1)); return; }
      if (e.key === "Tab") {
        const focusable = containerRef.current?.querySelectorAll("button");
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length, onClose, onNavigate]);

  const current = items[index];
  const { objectUrl, loading, error } = useProxyBlob(current?.url || null, retryToken);

  const saveBlobUrl = (href, revoke) => {
    const a = document.createElement("a");
    a.href = href; a.download = current.filename || (current.caption ? `${current.caption}.jpg` : "recce-photo.jpg");
    document.body.appendChild(a); a.click(); a.remove();
    if (revoke) window.URL.revokeObjectURL(href);
  };

  const download = async () => {
    setDownloadFailed(false);
    if (objectUrl) { saveBlobUrl(objectUrl, false); return; }
    setDownloading(true);
    try {
      const r = await api.get(current.url, { responseType: "blob", silent401: true });
      saveBlobUrl(window.URL.createObjectURL(r.data), true);
    } catch { setDownloadFailed(true); }
    finally { setDownloading(false); }
  };

  if (!current) return null;
  return (
    <div className="ag-lightbox-overlay" data-testid="agency-lightbox" onClick={onClose}
      role="dialog" aria-modal="true" aria-label={current.caption ? `Photo: ${current.caption}` : "Photo viewer"} ref={containerRef}>
      <span className="ag-lightbox-counter">{index + 1} / {items.length}</span>
      <button className="ag-lightbox-close" onClick={onClose} data-testid="agency-lightbox-close" aria-label="Close">
        <X size={18} strokeWidth={1.75} />
      </button>
      {index > 0 && (
        <button className="ag-lightbox-nav ag-lightbox-prev" data-testid="agency-lightbox-prev" aria-label="Previous"
          onClick={(e) => { e.stopPropagation(); onNavigate(index - 1); }}>
          <ChevronLeft size={22} strokeWidth={1.75} />
        </button>
      )}
      {objectUrl && !renderFailed && (
        <img className="ag-lightbox-img" data-testid="agency-lightbox-img" src={objectUrl} alt={current.caption || "Behind the scenes photo"}
          onError={() => setRenderFailed(true)} onClick={(e) => e.stopPropagation()} />
      )}
      {objectUrl && renderFailed && (
        <div className="ag-lightbox-status" onClick={(e) => e.stopPropagation()} data-testid="agency-lightbox-render-error">
          <p>Your browser can't display this image format (HEIC/HEIF files from iPhones can't be shown in a browser).</p>
          <button className="ag-link" onClick={download} data-testid="agency-lightbox-render-error-download">Download it instead</button>
        </div>
      )}
      {/* RS-15 fix (2026-10) — loading/error states so a failed or slow fetch is never just a
          blank black backdrop with no feedback at all. */}
      {loading && !objectUrl && (
        <div className="ag-lightbox-status" onClick={(e) => e.stopPropagation()} data-testid="agency-lightbox-loading">Loading photo…</div>
      )}
      {error && (
        <div className="ag-lightbox-status" onClick={(e) => e.stopPropagation()} data-testid="agency-lightbox-error">
          <p>Couldn't load this photo.</p>
          <button className="ag-link" onClick={() => setRetryToken((n) => n + 1)} data-testid="agency-lightbox-retry">Try again</button>
        </div>
      )}
      {downloadFailed && (
        <div className="ag-lightbox-status" onClick={(e) => e.stopPropagation()} data-testid="agency-lightbox-download-error">
          <p>Couldn't download this photo.</p>
          <button className="ag-link" onClick={download} data-testid="agency-lightbox-download-retry">Try again</button>
        </div>
      )}
      {/* Part 5 (2026-10) — permanent, for every photo: it used to render only once the blob
          had loaded, so a photo that was slow, failed to fetch or couldn't be decoded had no
          download affordance at all. */}
      <button className="ag-lightbox-download" onClick={(e) => { e.stopPropagation(); download(); }} disabled={downloading}
        data-testid="agency-lightbox-download" aria-label="Download photo" title="Download">
        {downloading ? <Loader2 size={18} strokeWidth={1.75} /> : <Download size={18} strokeWidth={1.75} />}
      </button>
      {index < items.length - 1 && (
        <button className="ag-lightbox-nav ag-lightbox-next" data-testid="agency-lightbox-next" aria-label="Next"
          onClick={(e) => { e.stopPropagation(); onNavigate(index + 1); }}>
          <ChevronRight size={22} strokeWidth={1.75} />
        </button>
      )}
      {current.caption && <div className="ag-lightbox-caption" onClick={(e) => e.stopPropagation()}>{current.caption}</div>}
    </div>
  );
}

export function useThumbBlob(url) { return useProxyBlob(url); }
